import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Ruler, Users, Layers, Sparkles, ArrowUpRight } from "lucide-react";
import { Seo } from "@/components/Seo";
import { Section, SectionHeader } from "@/components/Section";
import { COMPANY, PROCESS } from "@/lib/site";

const VALUES = [
  { icon: Layers, title: "Eight Trades, One Team", body: "Tile, bath, kitchen, painting, flooring, electrical, drywall, carpentry — all in-house." },
  { icon: Users, title: "In-House Crews", body: "No subcontractor lottery — our trades, our standards." },
  { icon: Ruler, title: "Precision Craft", body: "Tight tolerances, level lines, and finishes that hold up." },
  { icon: Sparkles, title: "Clean Job Sites", body: "Daily site cleanup so your home stays liveable through the build." },
];

export default function About() {
  return (
    <div data-testid="about-page">
      <Seo
        title="About"
        description="IVrenovation is a renovation and construction team in the Durham Region & GTA — tile, bath, kitchen, painting, flooring, electrical, drywall, carpentry. Built with precision."
      />
      {/* Intro */}
      <Section>
        <div className="grid grid-cols-12 gap-8 items-end">
          <div className="col-span-12">
            <SectionHeader
              overline={`About ${COMPANY.name}`}
              title="A renovation company obsessed with the millimetre."
              description={`Founded by tradesmen, ${COMPANY.name} delivers renovation and construction services across the Durham Region and the GTA. We believe construction is craftsmanship — not a commodity.`}
            />
          </div>
        </div>

        <div className="mt-16 grid grid-cols-12 gap-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="col-span-12 md:col-span-7 aspect-[4/3] overflow-hidden border border-zinc-200"
          >
            <img
              src="https://images.unsplash.com/photo-1587582423116-ec07293f0395?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400"
              alt="Construction worker framing"
              className="w-full h-full object-cover"
            />
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="col-span-12 md:col-span-5 aspect-[4/3] overflow-hidden border border-zinc-200"
          >
            <img
              src="https://images.unsplash.com/photo-1694522362256-6c907336af43?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000"
              alt="Craftsman using a grinder"
              className="w-full h-full object-cover"
            />
          </motion.div>
        </div>
      </Section>

      {/* Values */}
      <Section className="bg-zinc-50">
        <SectionHeader
          overline="Why us"
          title="The four promises we make on every job."
        />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-zinc-200 border border-zinc-200 mt-14">
          {VALUES.map((v, idx) => (
            <motion.div
              key={v.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: idx * 0.05 }}
              className="bg-white p-8"
              data-testid={`value-card-${idx}`}
            >
              <v.icon className="w-7 h-7 text-orange-600" />
              <h3 className="font-display text-xl mt-6 tracking-tight">{v.title}</h3>
              <p className="text-sm text-zinc-600 mt-3 leading-relaxed">{v.body}</p>
            </motion.div>
          ))}
        </div>
      </Section>

      {/* Process */}
      <Section dark>
        <p className="overline" style={{ color: "#fb923c" }}>Process</p>
        <h2 className="font-display mt-4 text-3xl md:text-4xl lg:text-5xl tracking-tighter leading-[1.05] text-white max-w-3xl">
          A clear path from first call to final walkthrough.
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-px bg-white/10 mt-14">
          {PROCESS.map((p) => (
            <div key={p.step} className="bg-graphite p-8">
              <p className="font-display text-5xl text-orange-500">{p.step}</p>
              <h3 className="font-display text-xl mt-6 text-white">{p.title}</h3>
              <p className="mt-3 text-sm text-zinc-400 leading-relaxed">{p.body}</p>
            </div>
          ))}
        </div>
      </Section>

      <section className="bg-orange-600 text-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-20 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
          <h2 className="font-display text-3xl md:text-5xl tracking-tighter">Let’s talk about your project.</h2>
          <Link
            to="/contact"
            className="bg-zinc-950 text-white px-8 py-5 text-sm font-bold uppercase tracking-wider inline-flex items-center gap-2 hover:bg-white hover:text-zinc-950 transition w-fit"
            data-testid="about-cta-contact"
          >
            Contact us <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
